<main class="grow" role="content">
  <div class="kt-container-fixed swaps-wide-container py-5 lg:py-7.5">
    <div class="kt-card">
      <div class="kt-card-header min-h-16 flex-wrap gap-3">
        <h1 class="kt-card-title">Swaps</h1>

        <div class="inline-flex items-center gap-2.5 flex-wrap">
          <input
            type="text"
            class="kt-input sm:w-72"
            placeholder="Search swaps..."
            [attr.data-kt-datatable-search]="'#' + datatableId"
            aria-label="Search swaps"
          />

          <button
            type="button"
            class="kt-btn kt-btn-outline"
            (click)="onRetry()"
            [disabled]="isLoading()"
          >
            <i class="ki-filled ki-arrows-circle text-sm" aria-hidden="true"></i>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div class="kt-card-content p-0">
        @if (isLoading()) {
          <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading swaps">
            <span class="catalog-loading-spinner" aria-hidden="true"></span>
            <span class="catalog-loading-text">Loading...</span>
          </div>
        }

        @if (errorMessage()) {
          <div class="p-5 lg:p-7.5">
            <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
              <span>{{ errorMessage() }}</span>
              <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="onRetry()">Retry</button>
            </div>
          </div>
        }

        @if (!errorMessage() && !isLoading()) {
          <div
            [id]="datatableId"
            class="kt-card-table swaps-compact-table"
            data-kt-datatable="true"
            data-kt-datatable-page-size="25"
            data-kt-datatable-state-save="true"
            data-kt-datatable-state-namespace="swaps-datatable"
          >
            <div class="kt-table-wrapper kt-scrollable">
              <table class="kt-table" data-kt-datatable-table="true">
                <thead>
                  <tr>
                    <th scope="col" data-kt-datatable-column="dealId" class="swaps-id-header">
                      <span class="kt-table-col">
                        <span class="kt-table-col-label">Deal Id</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                    <th scope="col" data-kt-datatable-column="tradeId" class="swaps-id-header">
                      <span class="kt-table-col">
                        <span class="kt-table-col-label">Trade Id</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                    <th scope="col" data-kt-datatable-column="structureId" class="swaps-id-header">
                      <span class="kt-table-col">
                        <span class="kt-table-col-label">Structure Id</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                    <th scope="col" data-kt-datatable-column="counterparty">
                      <span class="kt-table-col">
                        <span class="kt-table-col-label">Counterparty</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                    <th scope="col" data-kt-datatable-column="folder">
                      <span class="kt-table-col">
                        <span class="kt-table-col-label">Folder</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                    <th scope="col" data-kt-datatable-column="tradeDate" data-kt-datatable-sort-raw>
                      <span class="kt-table-col">
                        <span class="kt-table-col-label">Trade Date</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                    <th scope="col" data-kt-datatable-column="settlementDate" data-kt-datatable-sort-raw>
                      <span class="kt-table-col">
                        <span class="kt-table-col-label">Settlement Date</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                    <th scope="col" data-kt-datatable-column="maturityDate" data-kt-datatable-sort-raw>
                      <span class="kt-table-col">
                        <span class="kt-table-col-label">Maturity Date</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                    <th scope="col" data-kt-datatable-column="payLegNotional" data-kt-datatable-sort-raw class="text-end">
                      <span class="kt-table-col justify-end">
                        <span class="kt-table-col-label">Pay Leg Notional</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                    <th scope="col" data-kt-datatable-column="receiveLegNotional" data-kt-datatable-sort-raw class="text-end">
                      <span class="kt-table-col justify-end">
                        <span class="kt-table-col-label">Receive Leg Notional</span>
                        <span class="kt-table-col-sort"></span>
                      </span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  @if (rows().length === 0) {
                    <tr>
                      <td colspan="10" class="text-center text-secondary-foreground py-6">
                        No swaps found.
                      </td>
                    </tr>
                  } @else {
                    @for (item of rows(); track trackByRow($index, item)) {
                      <tr>
                        <td class="swaps-id-cell">{{ item.dealId || '-' }}</td>
                        <td class="swaps-id-cell">
                          @if (item.tradeId) {
                            <a
                              class="text-primary hover:underline"
                              [routerLink]="['/swap-detail', item.tradeId]"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {{ item.tradeId }}
                            </a>
                          } @else {
                            -
                          }
                        </td>
                        <td class="swaps-id-cell">{{ item.structureId || '-' }}</td>
                        <td>{{ item.counterparty || '-' }}</td>
                        <td>{{ item.folder || '-' }}</td>
                        <td><span [attr.data-sort-value]="dateSortValue(item.tradeDate)">{{ formatDate(item.tradeDate) }}</span></td>
                        <td><span [attr.data-sort-value]="dateSortValue(item.settlementDate)">{{ formatDate(item.settlementDate) }}</span></td>
                        <td><span [attr.data-sort-value]="dateSortValue(item.maturityDate)">{{ formatDate(item.maturityDate) }}</span></td>
                        <td class="text-end"><span [attr.data-sort-value]="amountSortValue(item.payLegNotional)">{{ formatNotional(item.payLegCurrency, item.payLegNotional) }}</span></td>
                        <td class="text-end"><span [attr.data-sort-value]="amountSortValue(item.receiveLegNotional)">{{ formatNotional(item.receiveLegCurrency, item.receiveLegNotional) }}</span></td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>

            <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
              <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>

              <div class="kt-datatable-length flex items-center gap-3">
                <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
                <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</main>